import type { SmtpAddressInput } from "~admin/email/helpers/smtpAddressListEditor";

import { TextField } from "@mui/material";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelButton, SubmitButton } from "~/components/Buttons";
import FormModal from "~/components/FormModal";
import SelectNative from "~/components/select/SelectNative";
import { BodyTextSmall } from "~/components/Typographies";
import { type SetTenantSmtpConnectionInput, SmtpFromMode } from "~/gql/graphqlBase.generated";
import { type GetTenantSmtpConnectionQuery } from "~/gql/queries/tenantSmtpConnection.generated";
import useDiscardChangesConfirm from "~/hooks/useDiscardChangesConfirm";
import useReportDirty from "~/hooks/useReportDirty";
import SmtpAddressListEditor from "~admin/email/components/SmtpAddressListEditor";
import { canAddSmtpAddress } from "~admin/email/helpers/smtpAddressListEditor";
import {
	buildSmtpAddressesPayload,
	canSubmitSmtpConnectionForm,
	hasSmtpConnectionEdits,
	smtpConnectionDraft,
	smtpSingleAddress
} from "~admin/email/helpers/smtpConnectionForm";
import { SMTP_FROM_MODE_DETAILS } from "~admin/email/helpers/smtpConnectionPresentation";

export type TenantSmtpConnection = NonNullable<GetTenantSmtpConnectionQuery["tenantSmtpConnection"]>;

interface SmtpSetupModalProps {
	initialConnection?: TenantSmtpConnection | null;
	onClose: () => void;
	onDirtyChange: (dirty: boolean) => void;
	onSubmit: (input: SetTenantSmtpConnectionInput) => Promise<boolean>;
	open: boolean;
	returnControl?: ReactNode;
	saving: boolean;
}

const SmtpSetupModal = ({
	initialConnection,
	onClose,
	onDirtyChange,
	onSubmit,
	open,
	returnControl,
	saving
}: SmtpSetupModalProps) => {
	const { t } = useTranslation("common");
	const confirmDiscard = useDiscardChangesConfirm();
	const [baseline] = useState(() => smtpConnectionDraft(initialConnection));
	const [host, setHost] = useState(baseline.host);
	const [port, setPort] = useState(baseline.port);
	const [username, setUsername] = useState(baseline.username);
	const [password, setPassword] = useState("");
	const [fromMode, setFromMode] = useState<SmtpFromMode>(baseline.fromMode);
	const [addresses, setAddresses] = useState<SmtpAddressInput[]>(baseline.addresses);

	const isEditing = !!initialConnection;

	const isSingleAccountMode = fromMode === SmtpFromMode.SingleAccount;
	const fromModeDescriptionKey = SMTP_FROM_MODE_DETAILS.find((detail) => detail.value === fromMode)?.descriptionKey;
	const showAddressEditor = !isSingleAccountMode;
	const singleAddressEmail = addresses[0]?.email ?? "";
	const singleAddressValid = canAddSmtpAddress(singleAddressEmail, []);
	const anyWithListEmptyError =
		fromMode === SmtpFromMode.AnyWithList && addresses.length === 0
			? "Any-address mode requires at least one address in the list."
			: undefined;
	const canSubmit =
		!saving &&
		canSubmitSmtpConnectionForm({
			addressCount: addresses.length,
			fromMode,
			host,
			password,
			port,
			singleAddressValid,
			username
		});

	const isDirty = hasSmtpConnectionEdits(baseline, { addresses, fromMode, host, port, username }, password.length > 0);

	useReportDirty(isDirty, onDirtyChange);

	const handleClose = async () => {
		if (isDirty && !(await confirmDiscard())) return;
		onClose();
	};

	const handleSubmit = async () => {
		if (!canSubmit) return;
		const ok = await onSubmit({
			addresses: buildSmtpAddressesPayload(fromMode, addresses),
			fromMode,
			host: host.trim(),
			password,
			port,
			username: username.trim()
		});
		if (ok) onClose();
	};

	return (
		<FormModal
			actions={
				<>
					<CancelButton onClick={handleClose} />
					<SubmitButton disabled={!canSubmit} onClick={handleSubmit}>
						{saving ? "Saving..." : isEditing ? "Save changes" : "Save connection"}
					</SubmitButton>
				</>
			}
			maxWidth="480px"
			onClose={handleClose}
			onSubmit={handleSubmit}
			open={open}
			title={isEditing ? "Edit SMTP connection" : "Set up SMTP"}
		>
			{returnControl}
			<TextField
				fullWidth
				label="SMTP host"
				onChange={(event) => setHost(event.target.value)}
				placeholder="smtp.yourprovider.com"
				sx={{ mb: 2 }}
				value={host}
			/>
			<TextField
				fullWidth
				label="Port"
				onChange={(event) => setPort(Number(event.target.value) || 0)}
				sx={{ mb: 2 }}
				type="number"
				value={port}
			/>
			<TextField
				fullWidth
				label="Username"
				onChange={(event) => setUsername(event.target.value)}
				placeholder="orders@yourcompany.com"
				sx={{ mb: 2 }}
				value={username}
			/>
			<TextField
				fullWidth
				helperText={isEditing ? t("admin.email.smtp.passwordReenterHelp") : "Never displayed back once saved."}
				label="Password"
				onChange={(event) => setPassword(event.target.value)}
				sx={{ mb: 2 }}
				type="password"
				value={password}
			/>
			<SelectNative
				label="From-address mode"
				noBlank
				onChange={(event) => setFromMode(event.target.value as SmtpFromMode)}
				options={SMTP_FROM_MODE_DETAILS.map((detail) => ({ label: detail.label, value: detail.value }))}
				sx={{ mb: 1 }}
				value={fromMode}
			/>
			<BodyTextSmall color="text.secondary" sx={{ mb: 2 }}>
				{fromModeDescriptionKey && t(fromModeDescriptionKey)}
			</BodyTextSmall>
			{isSingleAccountMode && (
				<TextField
					error={singleAddressEmail.length > 0 && !singleAddressValid}
					fullWidth
					helperText={singleAddressEmail.length > 0 && !singleAddressValid ? "Enter a valid email address" : undefined}
					label="From address"
					onChange={(event) =>
						setAddresses(event.target.value ? [smtpSingleAddress(event.target.value, baseline.addresses)] : [])
					}
					placeholder="orders@yourcompany.com"
					sx={{ mb: 2 }}
					value={singleAddressEmail}
				/>
			)}
			{showAddressEditor && (
				<SmtpAddressListEditor addresses={addresses} emptyListError={anyWithListEmptyError} onChange={setAddresses} />
			)}
		</FormModal>
	);
};

export default SmtpSetupModal;
